import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Calendar,
  DollarSign,
  Receipt,
  Users,
  Clock,
  CheckCircle2,
  AlertCircle,
  Eye,
  RefreshCw,
  ChevronRight,
  X,
  CreditCard,
  Smartphone,
  Banknote,
  Lock,
  Unlock,
  Printer,
  CalendarDays,
  CalendarRange,
  CheckCheck,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { formatPakistanDate, formatPakistanDateTime } from '../utils/pakistanTime';

function getThisMonth() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function getLastMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export default function MonthlyClosing() {
  const { user } = useAuth();
  const isCashier = user?.role === 'cashier';

  const [selectedMonth, setSelectedMonth] = useState(getThisMonth);
  const [loading, setLoading] = useState(false);
  const [closingData, setClosingData] = useState({
    month: selectedMonth,
    summary: {
      total_sales: 0,
      total_invoices: 0,
      cash_sales: 0,
      card_sales: 0,
      online_sales: 0,
      credit_sales: 0,
      total_cashiers: 0,
      closed_cashiers: 0,
      pending_closings: 0,
    },
    cashiers: [],
  });

  const [selectedCashier, setSelectedCashier] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [batchClosing, setBatchClosing] = useState(false);

  useEffect(() => {
    loadMonthlyClosing();
  }, [selectedMonth]);

  const loadMonthlyClosing = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/monthly-closings?month=${selectedMonth}`);
      const data = res?.data || res;
      if (data) {
        setClosingData({
          month: data.month || selectedMonth,
          summary: data.summary || {
            total_sales: 0,
            total_invoices: 0,
            cash_sales: 0,
            card_sales: 0,
            online_sales: 0,
            credit_sales: 0,
            total_cashiers: 0,
            closed_cashiers: 0,
            pending_closings: 0,
          },
          cashiers: Array.isArray(data.cashiers) ? data.cashiers : [],
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load monthly closing data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminCloseForCashier = async (cashier) => {
    if (!cashier) return;
    setActionLoading(true);
    try {
      const payload = {
        business_month: selectedMonth,
        staff_id: cashier.staff_id,
        notes: `Month closed by Admin (${user?.full_name || 'Admin'})`,
      };
      await api.post('/monthly-closings/close', payload);
      toast.success(`Closed month for ${cashier.staff_name} successfully!`);
      await loadMonthlyClosing();
      setSelectedCashier((prev) =>
        prev ? { ...prev, status: 'CLOSED', closed_at: new Date().toISOString() } : null
      );
    } catch (err) {
      toast.error(err.message || 'Failed to close month for cashier');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseAllCashiers = async () => {
    if (!window.confirm(`Are you sure you want to finalize and close monthly registers for all staff for ${formatMonthHeading(selectedMonth)}?`)) {
      return;
    }
    setBatchClosing(true);
    try {
      const payload = {
        business_month: selectedMonth,
        notes: `Batch monthly closing finalized by Admin (${user?.full_name || 'Admin'})`,
      };
      const res = await api.post('/monthly-closings/close-all', payload);
      toast.success(res?.message || 'All monthly registers closed successfully!');
      await loadMonthlyClosing();
    } catch (err) {
      toast.error(err.message || 'Failed to close all monthly registers');
    } finally {
      setBatchClosing(false);
    }
  };

  const formatMonthHeading = (monthStr) => {
    if (!monthStr) return '';
    try {
      const [y, m] = monthStr.split('-').map(Number);
      const dateObj = new Date(y, m - 1, 1);
      return dateObj.toLocaleDateString('en-GB', {
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return monthStr;
    }
  };

  const formatDateTime = (dtStr) => {
    if (!dtStr) return '—';
    try {
      const dt = new Date(dtStr);
      return dt.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dtStr;
    }
  };

  if (isCashier) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto h-12 w-12 text-amber-500 mb-3" />
          <h2 className="text-xl font-bold text-gray-900">Admin Access Only</h2>
          <p className="mt-2 text-sm text-gray-600">
            The full Monthly Closing register is restricted to Shop Owners and Admins.
            You can view your daily closings from POS or Sales History.
          </p>
        </div>
      </div>
    );
  }

  const { summary, cashiers } = closingData;
  const formattedMonthTitle = formatMonthHeading(selectedMonth);

  return (
    <div className="space-y-6 pb-12">
      {/* ── Closing Tabs Toggle (Daily vs Monthly) ── */}
      <div className="flex items-center gap-1.5 border-b border-gray-200 pb-3">
        <Link
          to="/daily-closing"
          className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition"
        >
          <CalendarDays className="h-4 w-4" />
          <span>Daily Closing</span>
        </Link>
        <Link
          to="/monthly-closing"
          className="flex items-center gap-2 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200/80 px-4 py-2 text-sm font-bold shadow-xs transition"
        >
          <CalendarRange className="h-4 w-4 text-emerald-600" />
          <span>Monthly Closing</span>
          <span className="rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-extrabold uppercase">
            Active
          </span>
        </Link>
      </div>

      {/* ── Page Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shadow-md shadow-emerald-500/20">
              <CalendarRange className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">Monthly Closing</h1>
              <p className="text-xs text-gray-500 font-medium">
                Review, audit, and finalize cashier shifts and register closings for the month
              </p>
            </div>
          </div>
        </div>

        {/* Month Selector Controls */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setSelectedMonth(getThisMonth())}
              className={clsx(
                "px-3 py-1.5 text-xs font-bold rounded-lg transition-colors",
                selectedMonth === getThisMonth()
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              This Month
            </button>
            <button
              type="button"
              onClick={() => setSelectedMonth(getLastMonth())}
              className={clsx(
                "px-3 py-1.5 text-xs font-bold rounded-lg transition-colors",
                selectedMonth === getLastMonth()
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              Last Month
            </button>
          </div>

          <div className="relative">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-semibold text-gray-800 shadow-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <button
            type="button"
            onClick={loadMonthlyClosing}
            disabled={loading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-emerald-600 disabled:opacity-50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={clsx("h-4 w-4", loading && "animate-spin text-emerald-600")} />
          </button>

          {summary.pending_closings > 0 && (
            <button
              type="button"
              onClick={handleCloseAllCashiers}
              disabled={batchClosing}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-700 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:bg-emerald-800 transition active:scale-95 disabled:opacity-50"
            >
              <CheckCheck className="h-4 w-4" />
              <span>{batchClosing ? 'Closing All...' : 'Close All Open for Month'}</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Selected Month Banner ── */}
      <div className="flex items-center justify-between rounded-2xl bg-gradient-to-r from-emerald-800 via-teal-800 to-slate-900 px-6 py-4 text-white shadow-lg shadow-emerald-900/10">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-200 block">
            Selected Business Month
          </span>
          <span className="text-xl font-extrabold tracking-tight">
            {formattedMonthTitle}
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-2 rounded-xl bg-white/10 px-3.5 py-1.5 backdrop-blur-md text-xs font-medium text-emerald-100">
          <Calendar className="h-4 w-4 text-emerald-300" />
          <span>Showing Monthly Cashier Closings & Reconciliation</span>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Sales */}
        <div className="group rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-emerald-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Monthly Total Sales</span>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-100">
              <DollarSign className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-gray-900 tracking-tight">
              Rs. {Number(summary.total_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-bold text-emerald-700 border border-emerald-200/60">
                Cash: Rs. {Number(summary.cash_sales || 0).toLocaleString('en-PK')}
              </span>
              <span className="rounded bg-indigo-50 px-1.5 py-0.5 font-bold text-indigo-700 border border-indigo-200/60">
                Digital: Rs. {Number((summary.card_sales || 0) + (summary.online_sales || 0)).toLocaleString('en-PK')}
              </span>
              <span className="rounded bg-amber-50 px-1.5 py-0.5 font-bold text-amber-700 border border-amber-200/60">
                Credit: Rs. {Number(summary.credit_sales || 0).toLocaleString('en-PK')}
              </span>
            </div>
          </div>
        </div>

        {/* Total Invoices */}
        <div className="group rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-indigo-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Monthly Invoices</span>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100">
              <Receipt className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-gray-900 tracking-tight">
              {summary.total_invoices || 0}
            </span>
            <p className="mt-1 text-xs text-gray-500 font-medium">Processed throughout {formattedMonthTitle}</p>
          </div>
        </div>

        {/* Closed Cashiers */}
        <div className="group rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-teal-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Closed Cashiers</span>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600 transition-colors group-hover:bg-teal-100">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-emerald-700 tracking-tight">
              {summary.closed_cashiers || 0} / {summary.total_cashiers || 0}
            </span>
            <p className="mt-1 text-xs text-gray-500 font-medium">Monthly closings finalized</p>
          </div>
        </div>

        {/* Pending Closings */}
        <div className="group rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-amber-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Pending Closings</span>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600 transition-colors group-hover:bg-amber-100">
              <Clock className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className={clsx(
              "text-2xl font-black tracking-tight",
              summary.pending_closings > 0 ? "text-amber-600" : "text-gray-900"
            )}>
              {summary.pending_closings || 0}
            </span>
            <p className="mt-1 text-xs text-gray-500 font-medium">Staff registers awaiting closing</p>
          </div>
        </div>
      </div>

      {/* ── Cashier Monthly Table ── */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between bg-gray-50/50">
          <div>
            <h3 className="text-base font-bold text-gray-900">Cashier Monthly Register</h3>
            <p className="text-xs text-gray-500">
              Click on any staff row to inspect full breakdown, record notes, or finalize monthly closing
            </p>
          </div>
          <span className="text-xs font-bold text-gray-500 bg-white border border-gray-200 px-3 py-1 rounded-full shadow-xs">
            {cashiers.length} {cashiers.length === 1 ? 'Cashier / Staff' : 'Cashiers / Staff'}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-6 py-3.5">Cashier</th>
                <th className="px-6 py-3.5">Role</th>
                <th className="px-6 py-3.5 text-center">Invoices</th>
                <th className="px-6 py-3.5 text-right">Cash</th>
                <th className="px-6 py-3.5 text-right">Digital</th>
                <th className="px-6 py-3.5 text-right">Credit</th>
                <th className="px-6 py-3.5 text-right">Total Sales</th>
                <th className="px-6 py-3.5 text-center">Closed At</th>
                <th className="px-6 py-3.5 text-center">Status</th>
                <th className="px-6 py-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-400">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent mb-2" />
                    <p className="text-xs font-medium">Loading monthly closing records...</p>
                  </td>
                </tr>
              ) : cashiers.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-400">
                    <AlertCircle className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                    <p className="text-sm font-semibold text-gray-600">No sales or cashier activity recorded</p>
                    <p className="text-xs text-gray-400 mt-1">
                      No invoices were found for {formattedMonthTitle}.
                    </p>
                  </td>
                </tr>
              ) : (
                cashiers.map((cashier) => {
                  const isClosed = cashier.status === 'CLOSED';
                  return (
                    <tr
                      key={cashier.staff_id}
                      onClick={() => setSelectedCashier(cashier)}
                      className="cursor-pointer transition-colors hover:bg-emerald-50/40 group"
                    >
                      {/* Cashier Name */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={clsx(
                            "flex h-9 w-9 items-center justify-center rounded-xl font-bold text-xs uppercase shadow-xs",
                            isClosed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                          )}>
                            {cashier.staff_name?.charAt(0) || 'C'}
                          </div>
                          <div>
                            <span className="font-bold text-gray-900 block group-hover:text-emerald-700 transition-colors">
                              {cashier.staff_name}
                            </span>
                            {cashier.username && (
                              <span className="text-xs text-gray-400">@{cashier.username}</span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td className="px-6 py-4">
                        <span className={clsx(
                          "inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-bold uppercase tracking-wider",
                          cashier.role?.toUpperCase() === 'ADMIN'
                            ? "bg-purple-100 text-purple-700"
                            : cashier.role?.toUpperCase() === 'PHARMACIST'
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        )}>
                          {cashier.role}
                        </span>
                      </td>

                      {/* Invoices */}
                      <td className="px-6 py-4 text-center font-bold text-gray-800">
                        {cashier.invoice_count}
                      </td>

                      {/* Cash */}
                      <td className="px-6 py-4 text-right font-medium text-emerald-600">
                        Rs. {Number(cashier.cash_sales || 0).toLocaleString('en-PK')}
                      </td>

                      {/* Digital */}
                      <td className="px-6 py-4 text-right font-medium text-blue-600">
                        Rs. {Number((cashier.card_sales || 0) + (cashier.online_sales || 0)).toLocaleString('en-PK')}
                      </td>

                      {/* Credit */}
                      <td className="px-6 py-4 text-right font-medium text-amber-600">
                        Rs. {Number(cashier.credit_sales || 0).toLocaleString('en-PK')}
                      </td>

                      {/* Total Sales */}
                      <td className="px-6 py-4 text-right font-black text-gray-900">
                        Rs. {Number(cashier.total_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* Closed At */}
                      <td className="px-6 py-4 text-center text-xs font-medium text-gray-600">
                        {cashier.closed_at ? formatDateTime(cashier.closed_at) : '—'}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 text-center">
                        <span className={clsx(
                          "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold tracking-wide uppercase shadow-xs",
                          isClosed
                            ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                            : "bg-amber-100 text-amber-700 border border-amber-200 animate-pulse"
                        )}>
                          {isClosed ? (
                            <>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              CLOSED
                            </>
                          ) : (
                            <>
                              <Clock className="h-3.5 w-3.5" />
                              OPEN
                            </>
                          )}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 group-hover:text-emerald-700">
                          <span>Details</span>
                          <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Cashier Detail Modal ── */}
      {selectedCashier && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-gray-100">
            {/* Modal Header */}
            <div className={clsx(
              "px-6 py-4 flex items-center justify-between text-white",
              selectedCashier.status === 'CLOSED'
                ? "bg-gradient-to-r from-emerald-600 to-teal-700"
                : "bg-gradient-to-r from-indigo-600 to-blue-700"
            )}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-md">
                  {selectedCashier.status === 'CLOSED' ? (
                    <Lock className="h-5 w-5 text-white" />
                  ) : (
                    <Clock className="h-5 w-5 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold leading-tight">
                    {selectedCashier.staff_name}
                  </h3>
                  <p className="text-xs text-white/80 font-medium">
                    {formattedMonthTitle} • {selectedCashier.role}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCashier(null)}
                className="rounded-lg p-1.5 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Status Banner */}
              <div className={clsx(
                "rounded-xl p-4 flex items-center justify-between border",
                selectedCashier.status === 'CLOSED'
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              )}>
                <div className="flex items-center gap-2.5">
                  {selectedCashier.status === 'CLOSED' ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  ) : (
                    <Clock className="h-5 w-5 text-amber-600 shrink-0" />
                  )}
                  <div>
                    <span className="font-extrabold text-sm uppercase tracking-wide block">
                      Status: {selectedCashier.status}
                    </span>
                    <span className="text-xs opacity-90 block">
                      {selectedCashier.status === 'CLOSED'
                        ? `Finalized on ${formatDateTime(selectedCashier.closed_at)}${selectedCashier.closed_by_name ? ` by ${selectedCashier.closed_by_name}` : ''}`
                        : "Shift is currently open for this month."}
                    </span>
                  </div>
                </div>
              </div>

              {/* Total Card */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-5 text-center">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-1">
                  Total Monthly Sales
                </span>
                <span className="text-3xl font-black text-gray-900 tracking-tight">
                  Rs. {Number(selectedCashier.total_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-xs text-gray-500 block mt-1">
                  Across {selectedCashier.invoice_count} invoices in {formattedMonthTitle}
                </span>
              </div>

              {/* Breakdown */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">
                  Payment Method Breakdown
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-gray-500">Cash Sales</span>
                      <Banknote className="h-4 w-4 text-emerald-500" />
                    </div>
                    <span className="text-lg font-black text-gray-900">
                      Rs. {Number(selectedCashier.cash_sales || 0).toLocaleString('en-PK')}
                    </span>
                  </div>

                  <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-gray-500">Card Sales</span>
                      <CreditCard className="h-4 w-4 text-blue-500" />
                    </div>
                    <span className="text-lg font-black text-gray-900">
                      Rs. {Number(selectedCashier.card_sales || 0).toLocaleString('en-PK')}
                    </span>
                  </div>

                  <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-gray-500">Online / Bank</span>
                      <Smartphone className="h-4 w-4 text-indigo-500" />
                    </div>
                    <span className="text-lg font-black text-gray-900">
                      Rs. {Number(selectedCashier.online_sales || 0).toLocaleString('en-PK')}
                    </span>
                  </div>

                  <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-gray-500">Udhaar (Credit)</span>
                      <Users className="h-4 w-4 text-amber-500" />
                    </div>
                    <span className="text-lg font-black text-gray-900">
                      Rs. {Number(selectedCashier.credit_sales || 0).toLocaleString('en-PK')}
                    </span>
                  </div>
                </div>
              </div>

              {selectedCashier.notes && (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600">
                  <span className="font-bold text-gray-700 block mb-0.5">Notes:</span>
                  {selectedCashier.notes}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-100 bg-gray-50/50 p-4 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs font-bold text-gray-700 shadow-xs hover:bg-gray-50 transition"
              >
                <Printer className="h-4 w-4 text-gray-500" />
                <span>Print Summary</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCashier(null)}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 transition"
                >
                  Close
                </button>

                {selectedCashier.status !== 'CLOSED' && (
                  <button
                    type="button"
                    onClick={() => handleAdminCloseForCashier(selectedCashier)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-xs hover:bg-emerald-700 transition active:scale-95 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{actionLoading ? 'Closing...' : 'Close Month for Staff'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
